/**
 * Bulk Action Dialog (iter269 — extracted from Enrollments.js).
 *
 * Multiplexes 4 different bulk operations (pause / resume / cancel /
 * updateFee) over the selected enrollments. Parent owns the action
 * type, the form state, and the execute handler — this component just
 * renders the right form for the action.
 *
 * Props:
 *   - open                 (bool)
 *   - onOpenChange         ((bool) => void)
 *   - actionType           ('pause' | 'resume' | 'cancel' | 'updateFee')
 *   - selectedCount        (number)
 *   - data                 (object — fields vary by actionType)
 *   - setData              ((updater | object) => void)
 *   - loading              (bool)
 *   - onExecute            (() => Promise<void>)
 */
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Loader2 } from 'lucide-react';

const TITLES = {
  pause: 'Bulk Pause Enrollments',
  resume: 'Bulk Resume Enrollments',
  cancel: 'Bulk Cancel Enrollments',
  updateFee: 'Bulk Update Fee/Frequency',
};

export default function BulkActionDialog({
  open,
  onOpenChange,
  actionType,
  selectedCount,
  data,
  setData,
  loading,
  onExecute,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{TITLES[actionType] || 'Bulk Action'}</DialogTitle>
          <DialogDescription>
            This action will affect {selectedCount} enrollment(s).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {actionType === 'pause' && (
            <>
              <div className="space-y-2">
                <Label>Pause Date</Label>
                <Input
                  type="date"
                  value={data.pause_date}
                  onChange={(e) => setData((d) => ({ ...d, pause_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Auto Resume Date (Optional)</Label>
                <Input
                  type="date"
                  value={data.auto_resume_date}
                  onChange={(e) => setData((d) => ({ ...d, auto_resume_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={data.notes}
                  onChange={(e) => setData((d) => ({ ...d, notes: e.target.value }))}
                  placeholder="Reason for pause..."
                />
              </div>
            </>
          )}

          {actionType === 'resume' && (
            <p className="text-sm text-slate-600">
              Are you sure you want to resume {selectedCount} enrollment(s)?
              This will set their status back to active.
            </p>
          )}

          {actionType === 'cancel' && (
            <>
              <div className="space-y-2">
                <Label>Cancel Date</Label>
                <Input
                  type="date"
                  value={data.cancel_date}
                  onChange={(e) => setData((d) => ({ ...d, cancel_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={data.notes}
                  onChange={(e) => setData((d) => ({ ...d, notes: e.target.value }))}
                  placeholder="Reason for cancellation..."
                />
              </div>
            </>
          )}

          {actionType === 'updateFee' && (
            <>
              <div className="space-y-2">
                <Label>Monthly Fee</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={data.monthly_fee}
                  onChange={(e) => setData((d) => ({ ...d, monthly_fee: e.target.value }))}
                  placeholder="Leave empty to keep current"
                />
              </div>
              <div className="space-y-2">
                <Label>Payment Frequency</Label>
                <Select
                  value={data.payment_frequency}
                  onValueChange={(v) => setData((d) => ({ ...d, payment_frequency: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Keep current" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Keep current</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={data.notes}
                  onChange={(e) => setData((d) => ({ ...d, notes: e.target.value }))}
                  placeholder="Note about the change..."
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={onExecute}
            disabled={loading}
            variant={actionType === 'cancel' ? 'destructive' : 'default'}
          >
            {loading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
            ) : (
              <>Apply to {selectedCount} Enrollment(s)</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
